import {useMemo, useState} from 'react';
import {Button, Form, Spinner} from 'react-bootstrap';
import {FaEdit, FaRedo, FaSmile, FaTrash} from 'react-icons/fa';
import {
    chatErrorMessage,
    decodeMentions,
    encodeMentions,
    groupedReactions,
    userDisplayName,
} from './chatUtils';
import AttachmentUploadQueue from './attachments/AttachmentUploadQueue';
import MessageAttachments from './attachments/MessageAttachments';
import useAttachmentUploads from './attachments/useAttachmentUploads';

export const REACTION_EMOJIS = ['👍', '❤️', '😂', '🎉', '👀', '✅'];

function initials(user) {
    return userDisplayName(user).split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase();
}

function timeLabel(value) {
    if (!value) return '';
    return new Intl.DateTimeFormat('en-AU', {hour: 'numeric', minute: '2-digit'}).format(new Date(value));
}

export function MessageComposer({conversation, onSend, placeholder = 'Message', disabled = false}) {
    const [body, setBody] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [mentionQuery, setMentionQuery] = useState(null);
    const uploads = useAttachmentUploads(conversation?.id || 0);
    const suggestions = useMemo(() => {
        if (mentionQuery === null) return [];
        const query = mentionQuery.toLowerCase();
        const members = (conversation?.members || []).map(member => member.user)
            .filter(user => user.username.toLowerCase().includes(query) || userDisplayName(user).toLowerCase().includes(query))
            .slice(0, 6);
        if (conversation?.kind === 'CHANNEL' && 'channel'.includes(query)) {
            return [{id: 'channel', username: 'channel', name: 'Notify everyone'}, ...members];
        }
        return members;
    }, [conversation, mentionQuery]);

    function updateBody(value) {
        setBody(value);
        const match = value.match(/(?:^|\s)@([A-Za-z0-9_.-]*)$/);
        setMentionQuery(match ? match[1] : null);
    }

    function insertMention(user) {
        setBody(current => current.replace(/@([A-Za-z0-9_.-]*)$/, `@${user.username} `));
        setMentionQuery(null);
    }

    async function submit() {
        const hasBody = Boolean(body.trim());
        if (submitting || disabled || !uploads.allReady || (!hasBody && uploads.items.length === 0)) return;
        const outgoing = hasBody ? encodeMentions(body.trim(), conversation.members, conversation.kind === 'CHANNEL') : null;
        const attachments = uploads.consumeReady();
        setBody('');
        setMentionQuery(null);
        setSubmitting(true);
        setError(null);
        try {
            await onSend(outgoing, attachments);
        } catch (sendError) {
            setError(sendError);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div
            className="chat-composer-wrap"
            onDragOver={event => {
                if (Array.from(event.dataTransfer.types).includes('Files')) event.preventDefault();
            }}
            onDrop={event => {
                event.preventDefault();
                if (!disabled) uploads.addFiles(event.dataTransfer.files);
            }}
        >
            {suggestions.length > 0 ? (
                <div className="chat-mention-menu" role="listbox" aria-label="Mention suggestions">
                    {suggestions.map(user => (
                        <button key={user.id} type="button" role="option" aria-selected="false" onClick={() => insertMention(user)}>
                            <strong>@{user.username}</strong><span>{user.name}</span>
                        </button>
                    ))}
                </div>
            ) : null}
            <Form.Control
                as="textarea"
                rows={2}
                value={body}
                maxLength={10000}
                disabled={disabled || submitting}
                aria-label={placeholder}
                placeholder={`${placeholder} — use @ to mention`}
                onChange={event => updateBody(event.target.value)}
                onKeyDown={event => {
                    if (event.key === 'Enter' && !event.shiftKey && mentionQuery === null) {
                        event.preventDefault();
                        submit();
                    }
                }}
            />
            <AttachmentUploadQueue uploads={uploads} disabled={disabled || submitting} />
            <div className="chat-composer-footer">
                <span className={body.length > 9500 ? 'text-danger' : 'text-muted'}>{body.length.toLocaleString()}/10,000</span>
                {error || uploads.validationError ? <span className="chat-composer-error" role="alert">{uploads.validationError || chatErrorMessage(error)}</span> : null}
                <Button
                    size="sm"
                    onClick={submit}
                    disabled={disabled || submitting || !uploads.allReady || (!body.trim() && uploads.items.length === 0)}
                >
                    {submitting ? <Spinner size="sm" /> : 'Send'}
                </Button>
            </div>
        </div>
    );
}

export function MessageItem({message, conversation, currentUser, onReply, onEdit, onDelete, onReaction, onRetry, renderActions}) {
    const [editing, setEditing] = useState(false);
    const [editBody, setEditBody] = useState('');
    const [showEmoji, setShowEmoji] = useState(false);
    const [working, setWorking] = useState(false);
    const [error, setError] = useState(null);
    const reactions = groupedReactions(message.reactions, currentUser?.id);
    const ownMessage = message.sender?.id === currentUser?.id;

    async function run(action) {
        setWorking(true);
        setError(null);
        try {
            await action();
        } catch (actionError) {
            setError(actionError);
        } finally {
            setWorking(false);
        }
    }

    if (message.message_type === 'SYSTEM') {
        return <div className="chat-system-message">{decodeMentions(message.body, conversation.members)}</div>;
    }

    return (
        <article className={`chat-message${message.pending ? ' is-pending' : ''}${message.send_error ? ' has-error' : ''}`}>
            <div className="chat-avatar" aria-hidden="true">{initials(message.sender)}</div>
            <div className="chat-message-content">
                <div className="chat-message-meta">
                    <strong>{userDisplayName(message.sender)}</strong>
                    <span>{timeLabel(message.created_at)}</span>
                    {message.edited_at ? <span>(edited)</span> : null}
                    {message.pending ? <span>Sending…</span> : null}
                </div>
                {message.deleted_at ? (
                    <div className="chat-deleted-message">This message was deleted.</div>
                ) : editing ? (
                    <div className="chat-inline-edit">
                        <Form.Control
                            as="textarea"
                            value={editBody}
                            maxLength={10000}
                            onChange={event => setEditBody(event.target.value)}
                        />
                        <div>
                            <Button size="sm" variant="outline-secondary" onClick={() => setEditing(false)}>Cancel</Button>
                            <Button
                                size="sm"
                                className="ms-2"
                                disabled={working || !editBody.trim()}
                                onClick={() => run(async () => {
                                    const encoded = encodeMentions(editBody.trim(), conversation.members, conversation.kind === 'CHANNEL');
                                    await onEdit(message, encoded);
                                    setEditing(false);
                                })}
                            >Save</Button>
                        </div>
                    </div>
                ) : (
                    message.body ? <div className="chat-message-body">{decodeMentions(message.body, conversation.members)}</div> : null
                )}
                {!message.deleted_at ? <MessageAttachments attachments={message.attachments || []} /> : null}
                {!message.deleted_at && !message.pending ? (
                    <div className="chat-message-actions">
                        {reactions.map(reaction => (
                            <button
                                key={reaction.emoji}
                                type="button"
                                className={`chat-reaction${reaction.reacted ? ' is-active' : ''}`}
                                onClick={() => run(() => onReaction(message, reaction.emoji, reaction.reacted))}
                            >
                                {reaction.emoji} {reaction.count}
                            </button>
                        ))}
                        <span className="chat-emoji-trigger">
                            <button type="button" aria-label="Add reaction" onClick={() => setShowEmoji(current => !current)}><FaSmile /></button>
                            {showEmoji ? (
                                <span className="chat-emoji-menu">
                                    {REACTION_EMOJIS.map(emoji => (
                                        <button key={emoji} type="button" onClick={() => {
                                            setShowEmoji(false);
                                            run(() => onReaction(message, emoji, reactions.some(item => item.emoji === emoji && item.reacted)));
                                        }}>{emoji}</button>
                                    ))}
                                </span>
                            ) : null}
                        </span>
                        {onReply ? <button type="button" onClick={() => onReply(message)}>Reply{message.reply_count ? ` (${message.reply_count})` : ''}</button> : null}
                        {ownMessage ? <button type="button" aria-label="Edit message" onClick={() => {
                            setEditBody(decodeMentions(message.body, conversation.members));
                            setEditing(true);
                        }}><FaEdit /></button> : null}
                        {renderActions?.({message, conversation, currentUser, run})}
                        <button type="button" aria-label="Delete message" onClick={() => {
                            if (window.confirm('Delete this message?')) run(() => onDelete(message));
                        }}><FaTrash /></button>
                    </div>
                ) : null}
                {message.send_error ? (
                    <button type="button" className="chat-retry-message" onClick={() => onRetry(message)}>
                        <FaRedo /> Retry sending
                    </button>
                ) : null}
                {error ? <div className="chat-message-error">{chatErrorMessage(error)}</div> : null}
            </div>
        </article>
    );
}
